import React, { useState } from 'react';
import { Box, Chip, Typography, Badge, Tooltip } from '@mui/material';
import { WarningAmber, RateReview, Groups } from '@mui/icons-material';

const tierConfig = {
  critical: { label: 'Critical', color: 'error', dotColor: '#d32f2f' },
  high: { label: 'High', color: undefined, dotColor: '#ed6c02' },
  moderate: { label: 'Moderate', color: 'warning', dotColor: '#ed6c02' },
  routine: { label: 'Routine', color: 'success', dotColor: '#2e7d32' },
};

const pulseKeyframes = {
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
    '70%': { boxShadow: '0 0 0 8px rgba(211, 47, 47, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
  },
};

function ColorDot({ color }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
}

function UrgencyStrip({ tiers, alerts, overdueReviews, caseload, onTierFilter }) {
  const [activeTier, setActiveTier] = useState(null);

  const handleTierClick = (tier) => {
    const newTier = activeTier === tier ? null : tier;
    setActiveTier(newTier);
    onTierFilter(newTier);
  };

  const safeTiers = tiers || { critical: 0, high: 0, moderate: 0, routine: 0 };
  const safeAlerts = alerts || { pending: 0, slaBreached: 0 };
  const safeCaseload = caseload || { total: 0, capacity: 0, utilization: 0 };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 60,
        px: 2,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        ...pulseKeyframes,
      }}
    >
      {/* Left side: tier pills */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Object.entries(tierConfig).map(([tier, config]) => {
          const count = safeTiers[tier] || 0;
          const isActive = activeTier === tier;
          const isCriticalPulsing = tier === 'critical' && count > 0;

          const chipSx = {
            fontWeight: 600,
            cursor: 'pointer',
            ...(tier === 'high' && !isActive
              ? {
                  borderColor: '#ed6c02',
                  color: '#ed6c02',
                }
              : {}),
            ...(tier === 'high' && isActive
              ? {
                  backgroundColor: '#ed6c02',
                  color: '#fff',
                  borderColor: '#ed6c02',
                  '&:hover': { backgroundColor: '#c55a02' },
                }
              : {}),
            ...(isCriticalPulsing
              ? { animation: 'pulse 2s infinite' }
              : {}),
          };

          return (
            <Tooltip key={tier} title={`Filter by ${config.label} urgency`}>
              <Chip
                icon={<ColorDot color={config.dotColor} />}
                label={`${count} ${config.label}`}
                size="small"
                variant={isActive ? 'filled' : 'outlined'}
                color={config.color}
                onClick={() => handleTierClick(tier)}
                sx={chipSx}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Right side: alerts, overdue, capacity */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {safeAlerts.pending > 0 && (
          <Tooltip title={`${safeAlerts.pending} pending alert${safeAlerts.pending !== 1 ? 's' : ''}`}>
            <Badge badgeContent={safeAlerts.pending} color="error" max={99}>
              <WarningAmber sx={{ color: '#ed6c02', fontSize: 22 }} />
            </Badge>
          </Tooltip>
        )}

        {safeAlerts.slaBreached > 0 && (
          <Tooltip title={`${safeAlerts.slaBreached} SLA breach${safeAlerts.slaBreached !== 1 ? 'es' : ''}`}>
            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 700 }}>
              {safeAlerts.slaBreached} SLA breached
            </Typography>
          </Tooltip>
        )}

        {overdueReviews > 0 && (
          <Tooltip title={`${overdueReviews} overdue review${overdueReviews !== 1 ? 's' : ''}`}>
            <Badge badgeContent={overdueReviews} color="warning" max={99}>
              <RateReview sx={{ color: '#ed6c02', fontSize: 22 }} />
            </Badge>
          </Tooltip>
        )}

        <Tooltip title={`${safeCaseload.total} of ${safeCaseload.capacity} capacity`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Groups sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: safeCaseload.utilization > 90 ? '#d32f2f' : 'text.secondary',
              }}
            >
              {safeCaseload.utilization}%
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default UrgencyStrip;
